import React from 'react';
import { ShieldAlert } from 'lucide-react';

interface AccessDeniedProps {
  /** Short explanation of what was denied and why, e.g. the reason the API gave. */
  message?: string;
  /** Optional secondary action, e.g. "Back to dashboard". */
  actionLabel?: string;
  onAction?: () => void;
}

/**
 * SAM-544 (N03): a coherent, non-blank denial view for a direct hit on a
 * route the current user isn't authorized for — replacing ProtectedRoute's
 * previous behavior of rendering `null` while it silently navigated away
 * (a blank flash, and a permanent blank screen if the navigate ever failed).
 * Mirrors the message the backend itself would give for the same denial
 * (see utils/apiErrors.ts's DEFAULT_FORBIDDEN_MESSAGE) so a route-level
 * denial and an action-level 403 read the same way.
 */
export const AccessDenied: React.FC<AccessDeniedProps> = ({
  message = 'You do not have permission to view this page.',
  actionLabel,
  onAction,
}) => {
  return (
    <div className="flex h-full min-h-[420px] w-full flex-col items-center justify-center gap-4 px-6 py-12 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--bad-bg)]">
        <ShieldAlert size={28} color="var(--danger)" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-base font-semibold text-[color:var(--ink-900)]">Access denied</p>
        <p className="max-w-sm text-sm text-[color:var(--ink-500)]">{message}</p>
      </div>
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="mt-2 rounded-lg bg-[color:var(--tenant-primary)] px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};
